import * as React from 'react';
import { useAuth } from '../auth';
import { Icon } from '../icons.jsx';
import { Badge, Button, Field } from '../ui.jsx';

export const ClaimReviewScreen = ({ go }) => {
  const auth = useAuth();
  const [claims, setClaims] = React.useState([]);
  const [reasons, setReasons] = React.useState({});
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [savingId, setSavingId] = React.useState('');

  const load = React.useCallback(async () => {
    if (!auth.account) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await auth.apiFetch('/api/provider-claim-reviews');
      setClaims(response.data);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [auth.account, auth.apiFetch]);

  React.useEffect(() => {
    load();
  }, [load]);

  const decide = async (claim, status) => {
    const reason = (reasons[claim.id] || '').trim();
    if (!reason) {
      setError('Add a review note before approving or rejecting a claim.');
      return;
    }
    setSavingId(claim.id);
    setError('');
    try {
      await auth.apiFetch(`/api/provider-claim-reviews/${claim.id}/decision`, {
        method: 'POST',
        body: JSON.stringify({ status, reason }),
      });
      setClaims((current) => current.filter((item) => item.id !== claim.id));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSavingId('');
    }
  };

  if (!auth.loading && !auth.account) {
    return (
      <div className="provider-access-state">
        <Icon name="shield" size={24}/>
        <h1>Administrator sign-in required</h1>
        <Button onClick={auth.signIn}>Sign in</Button>
      </div>
    );
  }

  return (
    <div className="claim-review-page route-in">
      <header className="claim-review-header">
        <div className="container">
          <button type="button" onClick={() => go('provider-access')}>
            <Icon name="chev-left" size={15}/> Provider account
          </button>
          <Badge tone="brand" icon="shield">Internal pilot operations</Badge>
          <h1>Review provider claims</h1>
          <p>Approve only after confirming the applicant is authorized to manage the official facility record.</p>
        </div>
      </header>
      <main className="container claim-review-content">
        {error && <div className="provider-access-error" role="alert">{error}</div>}
        {loading ? (
          <div className="provider-access-state">
            <span className="live-dot" aria-hidden="true"/>Loading pending claims…
          </div>
        ) : claims.length ? (
          <div className="claim-review-list">
            {claims.map((claim) => (
              <article key={claim.id}>
                <div className="claim-review-summary">
                  <div>
                    <span>Official provider</span>
                    <h2>{claim.providerName}</h2>
                    <small>{claim.providerId}</small>
                  </div>
                  <Badge tone="wait">Pending</Badge>
                </div>
                <dl>
                  <div><dt>Applicant</dt><dd>{claim.userDisplayName}</dd></div>
                  <div><dt>Verified email</dt><dd>{claim.userEmail}</dd></div>
                  <div><dt>Verified phone</dt><dd>{claim.verifiedPhoneE164}</dd></div>
                  <div><dt>Submitted</dt><dd>{new Date(claim.submittedAt).toLocaleString()}</dd></div>
                </dl>
                <Field label="Internal review note">
                  <textarea
                    rows={3}
                    value={reasons[claim.id] || ''}
                    onChange={(event) =>
                      setReasons((current) => ({ ...current, [claim.id]: event.target.value }))
                    }
                    placeholder="Record how authorization was confirmed."
                  />
                </Field>
                <div className="claim-review-actions">
                  <Button
                    variant="secondary"
                    disabled={savingId === claim.id}
                    onClick={() => decide(claim, 'rejected')}
                  >
                    Reject
                  </Button>
                  <Button
                    disabled={savingId === claim.id}
                    onClick={() => decide(claim, 'approved')}
                  >
                    Approve provider access
                  </Button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="provider-claim-empty">
            <Icon name="check-circle" size={26}/>
            <strong>No claims waiting</strong>
            <span>New verified submissions will appear here.</span>
          </div>
        )}
      </main>
    </div>
  );
};
